// src/modules/gym-partner/hooks/use-service-actions.ts

import { useCallback } from "react";
import { gymPartnerService } from "../services/gym.service";
import { GymService } from "../models/gym.model";
import { hasManagePermission } from "@modules/auth/utils/role.utils";
import { AuthArgs } from "../types/auth.types";

export function useServiceActions(
  setServices: React.Dispatch<React.SetStateAction<GymService[]>>,
  refresh: () => Promise<void>,
  authArgs: AuthArgs,
) {
  const createService = useCallback(
    async (data: Omit<GymService, "id" | "createdAt" | "updatedAt">) => {
      if (!hasManagePermission(authArgs.auth)) {
        throw new Error("You do not have permission to create a service.");
      }
      const newService = await gymPartnerService.createService(data, authArgs);
      setServices((prev) => [...prev, newService]);
      return newService;
    },
    [setServices, authArgs],
  );

  const updateService = useCallback(
    async (id: string, data: Partial<GymService> & { branchId: string }) => {
      if (!hasManagePermission(authArgs.auth)) {
        throw new Error("You do not have permission to update a service.");
      }
      const updated = await gymPartnerService.updateService(id, data, authArgs);
      if (updated) {
        setServices((prev) => prev.map((s) => (s.id === id ? updated : s)));
      }
      return updated;
    },
    [setServices, authArgs],
  );

  const deleteService = useCallback(
    async (id: string, branchId: string) => {
      if (!hasManagePermission(authArgs.auth)) {
        throw new Error("You do not have permission to delete a service.");
      }
      const success = await gymPartnerService.deleteService(
        id,
        branchId,
        authArgs,
      );
      if (success) {
        setServices((prev) => prev.filter((s) => s.id !== id));
        await refresh();
      }
      return success;
    },
    [setServices, refresh, authArgs],
  );

  return { createService, updateService, deleteService };
}
